import React, { useMemo } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import { useLocation, useParams, Link } from 'react-router-dom';
import trim from 'lodash/trim';

// GLOBAL FUNCTIONS
import { checkRole, isTrue, makePath } from 'functions.js';

// GLOBAL HELPERS/CONTEXT
import useRecordView from 'helpers/getRecordView';
import { useRecordContext } from 'helpers/getRecordData';

// VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { INTERFACE, RECORD } from 'pathnames.js';
import { NEW_USED } from 'codes';
import { INVENTORY_FILE_TYPE_IDS } from 'globals.js';
import { WARRANTY_TYPES_OPTION_KEYS } from '../../../../sections/variables.js';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';
import Card from 'parts/card/Card';

// LOCAL COMPONENTS
import AddVin from '../../../../tools/AddVin';
import SaveField from '../../../../tools/SaveField';
import Attachment from 'routers/interface/tools/attachment/Attachment';

// STYLES
import styles from './vehicleInfo.module.scss';

const VehicleInfoForm = () => {
  const location = useLocation();
  const { recordID } = useParams();

  const { isSales, isAmFinance, isOrder } = useRecordView();
  const { record } = useRecordContext() || {};

  const { values = {} } = useFormikContext() || {};
  const {
    current_mileage, //
    daily_sales_inventory_id,
    inventory,
    stock_num,
    subtype_name,
    warranty_type
  } = values || {};
  const {
    vin_num,
    type,
    vehicle_year,
    make,
    model,
    model_code,
    vehicle_trim,
    body_style,
    engine,
    engine_type,
    transmission_name,
    drivetrain_name,
    ext_color,
    int_color,
    age_of_vehicle,
    mileage,
    documentAttachment,
    inventoryDocumentAttachment,
    new_used
  } = useMemo(() => (!isSales ? values?.inventory || {} : values || {}), [values, isSales]);

  const showAddVin = useMemo(() => {
    return checkRole(['dslAdmin', 'salesManager', 'salesManagerPlus'])
      && trim(vin_num) === ''
      && (isOrder || isAmFinance)
      && isTrue(record?.display_add_vin_button);
  }, [isAmFinance, isOrder, record, vin_num]);

  const isNew = useMemo(() => parseInt(new_used) === NEW_USED.new.numeric, [new_used]);

  // console.log({ isAmFinance, isNew })

  return (
    <Form.Body>
      <Form.PlainText label="Sale Type" name="sale_type_category_name" />
      <Form.PlainText
        label="Stock #"
        className={styles.stock}
        as="span"
        value={
          <Link
            className="text-blue"
            to={makePath(INTERFACE.inventory, RECORD.view, daily_sales_inventory_id)}
            state={{ prevPath: location.pathname }}
          >
            {!isSales ? inventory?.stock_num : stock_num}
          </Link>
        }
        copyToClipboard
      />
      <Form.PlainText
        label="VIN #"
        value={showAddVin ? <AddVin /> : vin_num}
        text={
          !isSales && !vin_num ? (
            <span className="text-danger">VIN # missing. A sale cannot be delivered without a VIN.</span>
          ) : null
        }
        copyToClipboard={!showAddVin}
      />
      {!!vin_num && (
        isAmFinance ? (
          <Attachment.Upload
            label="Photo Proof of VIN"
            name="inventory.documentAttachment.vin_photo"
            documentType={isNew ? INVENTORY_FILE_TYPE_IDS.VIN_PHOTO_NEW : INVENTORY_FILE_TYPE_IDS.VIN_PHOTO_USED}
            uploadType="inventory"
          />
        ) : (
          <Attachment.Link 
            label="Photo Proof of VIN" 
            attachments={(isSales ? inventoryDocumentAttachment : documentAttachment)?.vin_photo} 
          />
        )
      )}
      <Form.PlainText label="Type" value={type} />
      {subtype_name ? <Form.PlainText label="Subtype" value={subtype_name} /> : null}
      <Form.PlainText label="Year" value={vehicle_year} />
      <Form.PlainText label="Make" value={make} />
      <Form.PlainText label="Model" value={model} />
      <Form.PlainText label="Model Code" value={model_code} />
      <Form.PlainText label="Trim" value={vehicle_trim} copyToClipboard />
      <Form.PlainText label="Body Style" value={body_style} />
      <Form.PlainText label="Engine" value={engine} />
      {!isSales ? <Form.PlainText label="Engine Type" value={engine_type} /> : null}
      <Form.PlainText label="Transmission" value={transmission_name} />
      <Form.PlainText label="Drivetrain" value={drivetrain_name} />
      <Form.PlainText label="Exterior" value={ext_color} copyToClipboard />
      <Form.PlainText label="Interior" value={int_color} copyToClipboard />
      <Form.PlainText label="Age" value={age_of_vehicle} />
      <SaveField
        useButton
        endpoint={ENDPOINTS.sales.updateWarrantyType(recordID)}
        params={{
          warrantyType: warranty_type
        }}
      >
        <Form.Select
          name="warranty_type"
          optionKeys={WARRANTY_TYPES_OPTION_KEYS}
          label="Warranty Type"
          lookup="warrantyTypes"
        />
      </SaveField>
      <Form.PlainText label="Mileage" value={mileage} schema="miles" copyToClipboard />
      <Form.PlainText label="Current Mileage" value={current_mileage} schema="miles" />
      
      {isAmFinance ? (
        <Attachment.Upload
          label="Photo Proof of Mileage"
          name="inventory.documentAttachment.mileage_photo"
          documentType={isNew ? INVENTORY_FILE_TYPE_IDS.MILEAGE_PHOTO_NEW : INVENTORY_FILE_TYPE_IDS.MILEAGE_PHOTO_USED}
          uploadType="inventory"
        />
      ) : (
        <Attachment.Link label="Photo Proof of Mileage" attachments={(isSales ? inventoryDocumentAttachment : documentAttachment)?.mileage_photo} />
      )}
    </Form.Body>
  );
};

// EXPORT
export default function VehicleInfo(props) {
  return <Card title="Vehicle Info" body={<VehicleInfoForm {...props} />} />;
}
